import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

// Компонент тултипа, который показывает описание колонки
const Tooltip = ({ content, position }) => {
  if (!position) return null;

  return createPortal(
    <div 
      style={{
        position: 'fixed',
        top: position.y - 10,
        left: position.x,
        transform: 'translate(-50%, -100%)',
      }}
      className="bg-gray-900 text-white rounded-lg shadow-lg p-3 text-sm max-w-xs z-[9999]"
    >
      {content}
      <div 
        className="absolute left-1/2 -bottom-1 w-2 h-2 bg-gray-900 transform -translate-x-1/2 rotate-45"
      />
    </div>,
    document.body
  );
};

// Вспомогательная функция для получения иконки сортировки
const getSortIcon = (columnKey, sortConfig) => {
  if (!sortConfig || sortConfig.key !== columnKey) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
  if (sortConfig.direction === 'asc') return <ArrowUp className="w-4 h-4" />;
  return <ArrowDown className="w-4 h-4" />;
};

// Словарь с описаниями для каждой колонки
const tooltipDescriptions = {
  date: "Date when errors occurred",
  total: "Total number of errors for this date"
  // Описания для типов ошибок будут добавляться динамически
};

const ErrorsTableHeader = ({ 
  id, 
  label, 
  onSort, 
  sortConfig,
  tooltipContent = null,
  isLast = false,
  width = null // Добавляем поддержку предопределенной ширины
}) => {
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const hasTooltip = tooltipContent || tooltipDescriptions[id] !== undefined;
  const tooltipText = tooltipContent || tooltipDescriptions[id];
  
  // Закрываем тултип при скролле
  useEffect(() => {
    const handleScroll = () => setTooltipPosition(null);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  const handleSortClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSort) {
      onSort(id);
    }
  };

  return (
    <th 
      className={`
        px-6 py-3 
        border-b border-r border-gray-200 last:border-r-0
        transition-all duration-150
        bg-gray-50
        select-none
      `}
      data-column-id={id}
      style={width ? { width: `${width}px` } : {}}
    >
      <div className="flex items-center justify-between">
        {/* Заголовок колонки */}
        <span 
          className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-grow cursor-pointer"
          onClick={handleSortClick}
        >
          {label}
        </span>
        
        <div className="flex items-center gap-2">
          {/* Иконка сортировки как отдельный кликабельный элемент */}
          {onSort && (
            <div 
              className="cursor-pointer p-1 hover:bg-gray-200 rounded"
              onClick={handleSortClick}
            >
              {getSortIcon(id, sortConfig)}
            </div>
          )}
          
          {/* Иконка подсказки */}
          {hasTooltip && (
            <div 
              className="inline-flex items-center justify-center cursor-help" 
              onClick={e => e.stopPropagation()}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.x + rect.width / 2,
                  y: rect.y
                });
              }}
              onMouseLeave={() => setTooltipPosition(null)}
            >
              <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </div>
          )}
        </div>
      </div>
      
      {hasTooltip && tooltipPosition && (
        <Tooltip 
          content={tooltipText} 
          position={tooltipPosition}
        />
      )}
    </th>
  );
};

export default ErrorsTableHeader;